<template>
  <div class="v2-basic-info">
    <h3 class="v2-basic-info__section-title">
      Basic Information
    </h3>

    <!-- Row 1: Style Code, Parent ID, Status -->
    <div class="v2-basic-info__row v2-basic-info__row--3col">
      <div class="v2-basic-info__field">
        <label class="v2-basic-info__label">
          Style Code
          <span class="v2-basic-info__required">*</span>
        </label>
        <validation-provider
          #default="{ errors }"
          name="Style Code"
          rules="required|min:1|max:100"
        >
          <input
            v-model="styleCode"
            class="v2-basic-info__input"
            :class="{ 'v2-basic-info__input--error': errors[0] }"
            placeholder="Enter Style Code"
          >
          <span
            v-if="errors[0]"
            class="v2-basic-info__error"
          >{{ errors[0] }}</span>
        </validation-provider>
      </div>
      <div class="v2-basic-info__field">
        <label class="v2-basic-info__label">Parent ID</label>
        <input
          v-model="uniqueId"
          class="v2-basic-info__input"
          placeholder="Enter Parent ID"
        >
      </div>
      <div class="v2-basic-info__field">
        <label class="v2-basic-info__label">Status</label>
        <div class="v2-basic-info__toggle-wrap">
          <span
            class="v2-basic-info__toggle-label"
            :class="{ 'v2-basic-info__toggle-label--muted': isLive }"
          >Draft</span>
          <button
            class="v2-basic-info__toggle"
            :class="{ 'v2-basic-info__toggle--active': isLive }"
            type="button"
            @click="isLive = !isLive"
          >
            <span class="v2-basic-info__toggle-track" />
            <span class="v2-basic-info__toggle-thumb" />
          </button>
          <span
            class="v2-basic-info__toggle-label"
            :class="{ 'v2-basic-info__toggle-label--muted': !isLive }"
          >Live</span>
        </div>
      </div>
    </div>

    <!-- Product Name -->
    <div class="v2-basic-info__field v2-basic-info__field--full">
      <label class="v2-basic-info__label">
        Product Name
        <span class="v2-basic-info__required">*</span>
      </label>
      <validation-provider
        #default="{ errors }"
        name="Product Name"
        rules="required|min:1|max:100"
      >
        <input
          v-model="name"
          class="v2-basic-info__input"
          :class="{ 'v2-basic-info__input--error': errors[0] }"
          placeholder="Enter Product Name"
        >
        <span
          v-if="errors[0]"
          class="v2-basic-info__error"
        >{{ errors[0] }}</span>
      </validation-provider>
    </div>

    <!-- Description -->
    <div class="v2-basic-info__field v2-basic-info__field--full">
      <label class="v2-basic-info__label">Product Description</label>
      <textarea
        v-model="descriptionLong"
        class="v2-basic-info__textarea"
        placeholder="Enter Product Description"
        rows="3"
      />
    </div>

    <!-- Material -->
    <div class="v2-basic-info__field v2-basic-info__field--full">
      <label class="v2-basic-info__label">Material Composition</label>
      <input
        v-model="descriptionMaterials"
        class="v2-basic-info__input"
        placeholder="Enter Material Composition"
      >
    </div>

    <!-- Row: Department, Category, Sub Category -->
    <div class="v2-basic-info__row v2-basic-info__row--3col">
      <div class="v2-basic-info__field">
        <label class="v2-basic-info__label">Department</label>
        <v2-dropdown
          :options="departmentOptions"
          :value="department && department[0]"
          placeholder="Select Department"
          :searchable="true"
          :allow-create="true"
          @select="onSelectDepartment"
        />
      </div>
      <div class="v2-basic-info__field">
        <label class="v2-basic-info__label">Category</label>
        <v2-dropdown
          :options="categoryOptions"
          :value="category && category[0]"
          placeholder="Select Category"
          :searchable="true"
          :allow-create="true"
          @select="onSelectCategory"
        />
      </div>
      <div class="v2-basic-info__field">
        <label class="v2-basic-info__label">Sub Category</label>
        <v2-dropdown
          :options="subCategoryDropdownOptions"
          :value="subCategory && subCategory[0]"
          placeholder="Select Sub Category"
          :searchable="true"
          :allow-create="true"
          @select="onSelectSubCategory"
        />
      </div>
    </div>

    <!-- Gender -->
    <div class="v2-basic-info__field">
      <label class="v2-basic-info__label">Gender</label>
      <v2-dropdown
        :options="genderOptions"
        :value="gender && gender[0]"
        placeholder="Select Gender"
        :searchable="true"
        :allow-create="true"
        @select="onSelectGender"
      />
    </div>
  </div>
</template>

<script>
import { useBasicInfo } from '@/views/apps/product/edit/input-sections/hooks/useBasicInfo'
import { productConstants } from '@kingpin-global/kingpin-utils-frontend'
import { ValidationProvider } from 'vee-validate'
import V2Dropdown from '@/components/v2/V2Dropdown.vue'

export default {
  name: 'V2BasicInformation',
  components: {
    ValidationProvider,
    V2Dropdown,
  },
  data() {
    return {
      departmentSearchOptions: [],
      categorySearchOptions: [],
      genderSearchOptions: [],
    }
  },
  computed: {
    departmentOptions() {
      const options = (productConstants.DEPARTMENTS || []).map(d => ({ label: d, value: d }))
      return [...options, ...this.departmentSearchOptions]
    },
    categoryOptions() {
      const options = (productConstants.CATEGORIES || []).map(c => ({ label: c, value: c }))
      return [...options, ...this.categorySearchOptions]
    },
    subCategoryDropdownOptions() {
      const raw = this.subCategoryOptions?.[0]?.options || []
      return raw.map(s => ({ label: s, value: s }))
    },
    genderOptions() {
      const options = (productConstants.GENDER || []).map(g => ({ label: g, value: g }))
      return [...options, ...this.genderSearchOptions]
    },
  },
  methods: {
    onSelectDepartment(val) {
      this.department = val ? [val] : []
    },
    onSelectCategory(val) {
      this.category = val ? [val] : []
    },
    onSelectSubCategory(val) {
      this.subCategory = val ? [val] : []
    },
    onSelectGender(val) {
      this.gender = val ? [val] : []
    },
  },
  setup() {
    const basicInfo = useBasicInfo()
    return { ...basicInfo }
  },
}
</script>

<style lang="scss" scoped>
.v2-basic-info {
  &__section-title {
    font-family: var(--v2-font-display);
    font-size: 1rem;
    font-weight: 600;
    color: var(--v2-foreground);
    margin-bottom: var(--v2-space-4);
    padding-bottom: var(--v2-space-2);
    border-bottom: 1px solid var(--v2-border);
  }

  &__row {
    display: flex;
    gap: var(--v2-space-4);
    margin-bottom: var(--v2-space-3);

    &--3col {
      > .v2-basic-info__field {
        flex: 1;
        min-width: 0;
      }
    }
  }

  &__field {
    margin-bottom: var(--v2-space-3);

    &--full {
      width: 100%;
    }
  }

  &__label {
    display: block;
    font-size: 0.75rem;
    font-weight: 600;
    color: var(--v2-muted-foreground);
    margin-bottom: var(--v2-space-1);
    text-transform: uppercase;
    letter-spacing: 0.03em;
  }

  &__required {
    color: var(--v2-destructive);
  }

  // Glass form fields — same recipe as V2Input so the page-local inputs
  // and textareas blend with V2Input usages elsewhere on the form.
  &__input {
    width: 100%;
    padding: var(--v2-space-2) var(--v2-space-3);
    border: 1px solid var(--v2-glass-stroke);
    border-radius: var(--v2-radius-md);
    font-size: 0.875rem;
    font-family: var(--v2-font-sans);
    color: var(--v2-foreground);
    // Translucent fill, no `backdrop-filter` — matches V2Input recipe.
    background: var(--v2-glass-surface-2);
    min-height: 38px;
    transition: background 0.15s ease, border-color 0.15s ease;

    &:hover:not(:disabled):not(:focus):not([readonly]) {
      border-color: var(--v2-glass-stroke-strong);
    }

    &:focus {
      outline: none;
      border-color: var(--v2-primary);
      box-shadow: 0 0 0 3px oklch(0.62 0.21 295 / 0.25);
    }

    &--error {
      border-color: var(--v2-destructive);
    }
  }

  &__textarea {
    width: 100%;
    padding: var(--v2-space-2) var(--v2-space-3);
    border: 1px solid var(--v2-glass-stroke);
    border-radius: var(--v2-radius-md);
    font-size: 0.875rem;
    font-family: var(--v2-font-sans);
    color: var(--v2-foreground);
    // Translucent fill, no `backdrop-filter` — matches V2Input recipe.
    background: var(--v2-glass-surface-2);
    resize: vertical;
    transition: background 0.15s ease, border-color 0.15s ease;

    &:hover:not(:disabled):not(:focus):not([readonly]) {
      border-color: var(--v2-glass-stroke-strong);
    }

    &:focus {
      outline: none;
      border-color: var(--v2-primary);
      box-shadow: 0 0 0 3px oklch(0.62 0.21 295 / 0.25);
    }
  }

  &__error {
    display: block;
    font-size: 0.6875rem;
    color: var(--v2-destructive);
    margin-top: var(--v2-space-1);
  }

  &__toggle-wrap {
    display: flex;
    align-items: center;
    gap: var(--v2-space-2);
    height: 36px;
  }

  &__toggle {
    position: relative;
    width: 40px;
    height: 22px;
    border: none;
    border-radius: 11px;
    background: var(--v2-border);
    cursor: pointer;
    padding: 0;
    transition: background 0.2s ease;

    &--active {
      background: var(--v2-primary);
    }
  }

  &__toggle-track {
    display: block;
    width: 100%;
    height: 100%;
  }

  &__toggle-thumb {
    position: absolute;
    top: 2px;
    left: 2px;
    width: 18px;
    height: 18px;
    border-radius: 50%;
    background: #fff;
    transition: transform 0.2s ease;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);

    .v2-basic-info__toggle--active & {
      transform: translateX(18px);
    }
  }

  &__toggle-label {
    font-size: 0.8125rem;
    color: var(--v2-foreground);
    font-weight: 500;

    &--muted {
      color: var(--v2-muted-foreground);
    }
  }
}
</style>
